import Image from "next/image";
import classnames from "classnames";
import Link from "next/link";
import { AiOutlineDollar } from "react-icons/ai";
import { FaChartLine } from "react-icons/fa";
import { SubmitHandler, useForm } from "react-hook-form";
import { HiOutlineSupport } from "react-icons/hi";
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import React, { useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

//local imports
import classes from "@/styles/become-a-partner/become-a-partner.module.css";
import { becomeAPartner } from "@/utils/content";
import { bapGr1Img, bapgr2Img1, bapgr2Img2 } from "@/utils/images";
import { routes } from "@/utils/routes";
import InputGroup from "../common/input-group";
import { partnerEmailService } from "@/services/connectWithUsServices";
import useAlertBar from "@/hooks/useAlertBar";
import { useIsMobile } from "@/hooks/useIsMobile";

type Inputs = {
  firstName: string;
  lastName: string;
  companyEmail: string;
  phone: string;
  companyName: string;
  location: string;
  comments: string;
  businessType: string;
};

const validationSchema = yup.object({
  companyEmail: yup
    .string()
    .email("Enter a valid email")
    .required("Email Address is required"),
  firstName: yup.string().required("Full Name is required"),
  lastName: yup.string().required("Last Name is required"),
  companyName: yup.string().required("Company Name is required"),
  location: yup.string().required("Location is required"),
  businessType: yup.string().required("Business Type is required"),
});

export default function BecomeAPartnerMain() {
  const content = becomeAPartner.en;
  const { openAlertBar } = useAlertBar();
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema) as any,
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setLoading(true);
    try {
      let res = await partnerEmailService(data);
      if (res.data.status) {
        setLoading(false);
        openAlertBar("Your request has been received, Thank you!", true);
      } else {
        setLoading(false);
        openAlertBar("Error occurred while sending your request", false);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
      openAlertBar("Error occurred while sending your request", false);
    }
  };

  const onBtnPress = () => {
    window.scrollTo(0, 1450);
  };

  return (
    <main className={classes.container}>
      <section className={classes.hero}>
        <h1>{content.title}</h1>
      </section>
      <section className={`${isMobile ? "" : "my-5"} container`}>
        <Row>
          <Col className={`${isMobile ? "mb-4" : "pt-5"}`} xs={12} md={7}>
            <h2 className="mt-5">{content.title2}</h2>
            <p>{content.para2}</p>
            <Button onClick={onBtnPress} className={classes.btn}>
              {content.btn1Text}
            </Button>
          </Col>
          <Col className="text-center mt-4" xs={12} md={4}>
            <Image
              src={bapGr1Img}
              alt={bapGr1Img}
              className="img-fluid"
              height={300}
              width={250}
            />
          </Col>
        </Row>
      </section>
      <section className="my-5 container">
        <h2 className="text-center py-5">{content.title3}</h2>
        <Row>
          <Col xs={12} lg={6}>
            <div className="text-center">
              <Image
                src={bapgr2Img1}
                alt={bapgr2Img1}
                className="img-fluid"
                height={190}
                width={200}
              />
              <h5 className="mt-4">{content.card1Title}</h5>
              <p className="w-75 mx-auto">{content.card1Para}</p>
              <Link
                className={`${classes.btn} btn`}
                href={routes.technologyPartner}
              >
                {content.card1BtnText}
              </Link>
            </div>
          </Col>
          <Col xs={12} lg={6}>
            <div className="text-center">
              <Image
                className="mt-4 img-fluid"
                src={bapgr2Img2}
                alt={bapgr2Img2}
                height={150}
                width={170}
              />
              <h5 className="mt-4 pt-3">{content.card2Title}</h5>
              <p className="w-75 mx-auto">{content.card2Para}</p>
              <Link
                className={`${classes.btn} btn`}
                href={routes.fulfillmentPartners}
              >
                {content.card2BtnText}
              </Link>
            </div>
          </Col>
        </Row>
      </section>
      <section className="my-5 container">
        <h2 className="text-center py-5">{content.title4}</h2>
        <Row>
          <Col xs={12} md={4}>
            <div className="text-center p-3">
              <AiOutlineDollar size={50} />
              <h5 className="mt-3">{content.grid1Title1}</h5>
              <p>{content.grid1Para1}</p>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="text-center p-3">
              <FaChartLine size={50} />
              <h5 className="mt-3">{content.grid1Title2}</h5>
              <p>{content.grid1Para2}</p>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="text-center p-3">
              <HiOutlineSupport size={50} />
              <h5 className="mt-3">{content.grid1Title3}</h5>
              <p>{content.grid1Para3}</p>
            </div>
          </Col>
        </Row>
      </section>
      <section className="mt-5 container">
        <Form className="text-center pb-5" onSubmit={handleSubmit(onSubmit)}>
          <h2>{content.formTitle}</h2>
          <p>{content.formPara}</p>
          <Card className={classes.formCard}>
            <CardBody>
              <Row>
                <Col xs={12} lg={6}>
                  <InputGroup
                    required
                    label={content.firstName}
                    name="firstName"
                    register={register}
                    error={errors.firstName?.message}
                  />
                </Col>
                <Col xs={12} lg={6}>
                  <InputGroup
                    required
                    label={content.lastName}
                    name="lastName"
                    register={register}
                    error={errors.lastName?.message}
                  />
                </Col>
              </Row>
              <div className="my-3">
                <InputGroup
                  required
                  label={content.companyEmail}
                  name="companyEmail"
                  register={register}
                  error={errors.companyEmail?.message}
                />
              </div>
              <div className="my-3">
                <InputGroup
                  required
                  label={content.companyName}
                  name="companyName"
                  register={register}
                  error={errors.companyName?.message}
                />
              </div>
              <div className="my-3">
                <InputGroup
                  label={content.phoneNumber}
                  name="phone"
                  register={register}
                  error={errors.phone?.message}
                />
              </div>
              <div className="my-3">
                <Label>
                  {content.typeOfBusiness}{" "}
                  <span className="text-danger">*</span>
                </Label>
                <select
                  className={classnames("form-control", {
                    "is-invalid": errors.businessType?.message,
                  })}
                  {...register("businessType")}
                >
                  <option disabled>Please Select</option>
                  <option>Warehouse</option>
                  <option>Technology Partner</option>
                  <option>Fulfillment Partner</option>
                  <option>Agency</option>
                </select>
                {errors.businessType && (
                  <div className="invalid-feedback">
                    {errors.businessType?.message}
                  </div>
                )}
              </div>
              <div className="my-3">
                <Label>
                  {content.locationOfAudience}
                  <span className="text-danger">*</span>
                </Label>
                <select
                  className={classnames("form-control", {
                    "is-invalid": errors.location?.message,
                  })}
                  {...register("location")}
                >
                  <option disabled>-- Please Select --</option>
                  <option>Canada</option>
                  <option>USA</option>
                  <option>Rest of World</option>
                </select>
                {errors.location && (
                  <div className="invalid-feedback">
                    {errors.location?.message}
                  </div>
                )}
              </div>
              <div className="my-3">
                <Label>
                  {content.comment} ({content.optional})
                </Label>
                <textarea
                  {...register("comments")}
                  className={classnames("form-control", {
                    "is-invalid": errors.comments?.message,
                  })}
                  placeholder={content.comment}
                ></textarea>
                {errors.comments && (
                  <div className="invalid-feedback">
                    {errors.comments?.message}
                  </div>
                )}
              </div>
              <div className="text-center">
                <Button className={classes.btn}>
                  {content.applyNow}
                  {loading && <Spinner size="sm" className="ms-3" />}
                </Button>
              </div>
            </CardBody>
          </Card>
        </Form>
      </section>
      <div className={classes.floatBottom}></div>
    </main>
  );
}
